import { useEffect, useRef, useState } from "react";
import { FeedbackButton } from "./FeedbackButton";
import {
  copiesPerValue,
  hasHalfUnit,
  DEFAULT_DECK_CONFIG,
  deckSizeLabel,
  drainsCards,
  enabledWildKinds,
  fullUnitCount,
  halfUnitPicksAtRandom,
  HALF_UNIT_WILDS,
  MIN_CARDS_PER_PLAYER,
  normalizeHouseRules,
  isHalfDeck,
  normalizeWilds,
  enabledStarfallEvents,
  STARFALL_EVENT_KINDS,
  STARFALL_EVENT_NAMES,
  starfallApplies,
  DROUGHT_MISSES_TO_MIST,
  BURNING_DECK_FIRST_BURN_AFTER,
  STREAK_TURNS_TO_IGNITE,
  type StarfallEventKind,
  WILD_KINDS,
  totalCardCount,
  wildCount,
  type DeckConfig,
  type HouseRules,
  type WildKind,
} from "@fiveout/core";

/** One line per Constellations event, for the sheet to show the ones in play. */
const CONSTELLATION_EVENT_RULES: Record<StarfallEventKind, string> = {
  comets:
    "**Comet Shower:** the next three cards from the deck of whoever lit the third star fly face up onto three random piles. Nothing judges them, so it's three cards gone.",
  storm: "**Card Storm:** every card on the table is swept up and dealt back onto the five piles in new orders and heights. Wilds sink to the bottom.",
  bounty:
    "**Bounty Pile:** two piles hold a star for a lap. A correct Higher or Lower on either, or a Match off by two, earns a free card on it. A Match off by one there earns four bonus cards, and a Five Out puts two on every other pile.",
  meteors:
    "**Meteor Strike:** three meteors hit three different piles. The first two burn the top card off theirs; the last, a big one, destroys every card on its pile. Meteors burn wilds too.",
  swarm:
    "**Meteor Swarm:** the top card of whoever lit the third star shatters into seven meteors. That card is gone, and the meteors go round every pile with cards, each burning off the top card, wild or not.",
};

interface RulesSection {
  title: string;
  /** Each entry is one bullet; the leading `**bold**` span (if any) is styled as a lead-in term. */
  items: string[];
}

/**
 * One bullet per wild kind, keyed so a kind added to `WILD_KINDS` fails to
 * compile here until it has a line. Only the kinds switched on at this table
 * are shown, in deck order.
 *
 * Swap says what it does and nothing about how to use it well. That is the
 * designer's instruction, not an omission: don't add strategy to it.
 */
const WILD_RULES: Record<WildKind, { name: string; rule: string }> = {
  smash: {
    name: "Smash",
    rule: "**Smash Card:** slams into the pile and destroys every card beneath it — those cards leave the game entirely. Any wild down there survives and stays in the pile.",
  },
  lightning: {
    name: "Lightning",
    rule: "**Lightning Card:** bolts arc to the other four piles and vaporize the top card of each. Its own pile is left intact underneath it, and a pile showing a wild throws the bolt off and keeps its card.",
  },
  swap: {
    name: "Swap",
    rule: "**Swap Card:** at the end of your turn, pick any two piles with cards on them and their top cards trade places. Nothing else moves, and neither card is judged. Shut piles can still be picked. Two Swaps means two swaps; go out that turn and they're lost. If your timer runs out, two piles are picked for you.",
  },
  peek: {
    name: "Peek",
    rule: "**Peek Card:** your next card turns face up on your deck, for the whole table to see, until your next move. Two Peeks show the same card.",
  },
};

/** "Smash", "Smash and Peek", "Smash, Swap and Peek". */
function listNames(names: string[]): string {
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "one", "2", "3": copy counts read better spelled out only at one. */
function spellCount(n: number): string {
  return n === 1 ? "one" : String(n);
}

/**
 * Which wilds are in THIS deck, in words: "one each of Smash and Peek", "2 each
 * of Smash, Lightning, Swap and Peek, plus 2 more picked at random each round".
 *
 * A half unit carries two wilds. With two or fewer kinds on that is one of each
 * and every kind has the same count; with more, which two is drawn at the deal,
 * so the sheet says so rather than naming kinds that may not be in the deck.
 */
function describeWildMix(config: DeckConfig, kinds: WildKind[]): string {
  const names = listNames(kinds.map((kind) => WILD_RULES[kind].name));
  const fulls = fullUnitCount(config.size);
  if (hasHalfUnit(config.size) && halfUnitPicksAtRandom(config.wilds)) {
    const drawn = `${spellCount(HALF_UNIT_WILDS)}`;
    return fulls === 0
      ? `${drawn} picked at random each round from ${names}`
      : `${spellCount(fulls)} each of ${names}, plus ${drawn} more picked at random each round`;
  }
  const each = fulls + (hasHalfUnit(config.size) ? 1 : 0);
  return kinds.length > 1 ? `${spellCount(each)} each of ${names}` : `${spellCount(each)} ${names}`;
}

/**
 * Builds the rules text for the deck actually in play.
 *
 * This screen used to be static, and every statement about deck composition and
 * scoring in it is now configurable: a half deck has 2 copies of each value and
 * one of each wild switched on, a triple deck has 12 and six of each, and any
 * wild can be switched off. Hardcoding "four of each value, two wilds, 1–4 players share one
 * deck" would teach a game nobody is playing.
 */
function buildSections(config: DeckConfig, houseRules: HouseRules, reference: boolean, composition?: string): RulesSection[] {
  const wilds = normalizeWilds(config.wilds);
  const copies = copiesPerValue(config.size);
  const total = totalCardCount(config);
  const wildTotal = wildCount(config);
  const kinds = enabledWildKinds(wilds);
  // The reference sheet (opened from the main menu) is about the game, not a table: every wild is explained.
  const ruleKinds = reference ? [...WILD_KINDS] : kinds;

  // Named per kind rather than as a total, because the total alone hides the
  // rule — and with the half unit drawing two of them, not every kind is even
  // certain to be in the deck at 0.5.
  const wildMix = kinds.length === 1 && wildTotal === 1 ? `the ${WILD_RULES[kinds[0]].name} Card` : describeWildMix(config, kinds);
  const compositionBullet = composition
    ? composition
    : wildTotal > 0
      ? `**${deckSizeLabel(config.size)} in play:** ${copies} of each value 1–13, plus ${wildTotal} wild card${wildTotal === 1 ? "" : "s"} (${wildMix}). ${total} cards in all.${
          drainsCards(wilds) ? "" : " Nothing removes cards from play without Smash or Lightning, so expect a long round."
        }`
      : `**${deckSizeLabel(config.size)} in play:** ${copies} of each value 1–13 and no wild cards at all — ${total} cards. Nothing removes cards from play, so expect a long round.`;

  // Which suits are actually on the table depends on the size: a half unit only
  // has room for two of them, so saying "four suits" would be wrong at 0.5.
  const suitBullet = composition
    ? "**Suits are a memory aid, not a rule.** Nothing ever compares suits, but telling one 7 from another is how you recognize a stack coming back around."
    : hasHalfUnit(config.size)
    ? `**Suits are a memory aid, not a rule.** ${
        config.size === 0.5
          ? "A half deck carries two suits — diamonds and spades — so each value appears once in each."
          : "Full decks carry all four suits; the extra half deck adds another diamond and spade of each value."
      } Nothing ever compares suits, but telling one 7 from another is how you recognize a stack coming back around.`
    : "**Suits are a memory aid, not a rule.** The four copies of each value are one per suit — diamonds, hearts, clubs and spades. Nothing ever compares suits, but telling one 7 from another is how you recognize a stack coming back around.";

  const sections: RulesSection[] = [
    {
      title: "The Goal",
      items: [
        "**Empty your deck first.** Your cards are face down and you never see them — you play blind, off the top.",
        "**A full deck holds four of each value 1–13** — one per suit — plus one of each wild switched on (Smash, Lightning, Swap and Peek), up to 56 cards. A half deck is two of each value, plus two wilds drawn at random each round from those switched on.",
        compositionBullet,
        suitBullet,
        `**Pick any deck you like.** Deck size is independent of how many are playing; the only rule is that everyone must be dealt at least ${MIN_CARDS_PER_PLAYER} cards.`,
      ],
    },
  {
    title: "Your Turn",
    items: [
      "Pick any pile, then call **Higher**, **Lower**, or **Match** against the card showing on it. Your top card flips onto that pile.",
      "**Right:** the card stays and your turn ends. **Wrong:** you take that whole pile into the bottom of your deck.",
      "**Took a pile? You restart it.** Your next card goes straight down on the empty space, no guess and no risk — nobody gets a free turn just for playing after you.",
      "You can never call Higher on a 13 or Lower on a 1 — those buttons are hidden.",
      "**Empty pile?** Lay a card there for free, no guess needed. A Wild on top counts as empty.",
    ],
  },
  {
    title: "Calling Match",
    items: [
      "**Exact hit — Five Out!** Place one free card on each of the other four piles. Five cards gone in a single turn, and those four are never judged.",
      "**Off by 1:** place 2 more cards on any piles you like — three cards out this turn.",
      "**Off by 2:** your card simply stays and the turn ends.",
      "**Off by 3 or more:** you take the pile.",
      "**Numbers wrap round for a Match:** 13 sits next to 1, so a 13 on a 1 is off by 1.",
    ],
  },
  {
    title: "Bonus Chains",
    items: [
      "**Correct by exactly 1** (Higher on a 3, played a 4) earns a free card on the same pile. Keep hitting +1 and the chain keeps going.",
      "**Played the same value** you guessed against? That's a duplicate — play again on the same pile with the same call, as many times as it repeats.",
      "**Bonus cards can't hurt you.** They always stay, whatever their value; an off-chain value just ends the chain.",
      "These chains apply to Higher and Lower only, never to Match.",
    ],
  },
  ];

  if (wildTotal > 0 || reference) {
    sections.push({
      title: "Wild Cards",
      items: [
        "Wilds have no value, so they're never judged against a guess.",
        ...(reference || (hasHalfUnit(config.size) && halfUnitPicksAtRandom(wilds))
          ? [
              `**A half deck holds ${spellCount(HALF_UNIT_WILDS)} wilds.** With more kinds switched on than that, the ${spellCount(HALF_UNIT_WILDS)} are picked at random from them each round${
                reference || !isHalfDeck(config.size) ? ", on top of the one of each kind every full deck holds" : ""
              }.`,
            ]
          : []),
        ...ruleKinds.map((kind) => WILD_RULES[kind].rule),
        "**You keep the pile you clear.** Once your wild has gone off, your next card lands straight on top of it — free, never judged. Two cards out of your deck, and the player after you inherits nothing.",
        "Wilds fire however they arrive — on a guess, mid-chain, as a free placement, or as a bonus card — and take that follow-up card every time.",
        "A bare wild counts as an empty space: if something uncovers one, the next player can lay a card on it for free.",
      ],
    });
  }

  // Streaks is a section of its own, Blue Shell included: it is a sub-option
  // that changes what tapping the deck does, so it reads best beside the rule it
  // modifies. Only described when in force, like the rest of this sheet; the
  // normalised rules already force Blue Shell off without Streaks.
  if (houseRules.jourdansStreaks) {
    const streakItems = [
      "**Jourdan's Streaks.** Finish five turns in a row without taking a pile and your deck catches fire.",
      reference
        ? "**Tap your burning deck** on your turn, then pick a player — or, with Blue Shell Mode on, the fire picks for you (see below)."
        : houseRules.blueShell
          ? "**Tap your burning deck** on your turn and the fire picks the target for you — see Blue Shell below."
          : "**Tap your burning deck** on your turn, then pick a player.",
      "Either way your own turn is unaffected — you play it on the full table.",
      "**They start their next turn with two piles shut**, the two smallest at that moment, and every pile opens again the moment that turn is over.",
      "**Gang up and it gets cheaper.** A second player marking the same target adds one more pile, not two; four is the most anyone can be shut out of, so a player already at four can't be marked again, and one pile is always left to play on.",
      "**Spending the fire puts it out.** Five more clean turns to earn it back — and a pile you take resets you to nothing.",
    ];
    if (reference || houseRules.blueShell) {
      streakItems.push(
        "## Blue Shell Mode",
        ...(reference ? ["An optional mode: the host switches it on for a table."] : []),
        "**It takes the choice away.** Tap your burning deck and the fire picks for you — no picker.",
        "**It chases the leader:** whoever has the fewest cards left. If several are tied for the lead, every one of them is marked.",
        "**Out in front on your own? You can't spend a streak at all.** The fire chases the leader, so while that's only you it has nowhere to go. Level with somebody and it hits the rest of the tie instead.",
      );
    }
    sections.push({ title: "Jourdan's Streaks", items: streakItems });
  }

  // Its own section: it is on by default, and it is a
  // whole second game of events, not a tweak to the turn.
  const constellationRules = normalizeHouseRules(houseRules);
  if (reference || starfallApplies(constellationRules)) {
    // The events this table plays, or all of them in the reference. A host can
    // switch any of them off, and a sheet describing an event the table never
    // draws is a sheet that is wrong about this table.
    const events = reference ? STARFALL_EVENT_KINDS : enabledStarfallEvents(constellationRules);
    const names = events.map((k) => STARFALL_EVENT_NAMES[k]);
    const listed = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0];
    const constellationItems = [
      "**Constellations.** Whenever a card makes three or more piles show the same value on top, a star lights. Four of a kind lights another.",
      "**A staircase lights one too:** four piles showing values in a row, like 5, 6, 7 and 8, on any piles in any order. A fifth in the row lights another, and so does moving the row up or down a step.",
      ...(reference || isHalfDeck(config.size)
        ? ["**On a half deck only a staircase lights a star:** it holds two of each card, so three can never match."]
        : []),
      events.length > 1
        ? `**Three stars and an event fires** at the end of that turn, drawn from ${listed}. You won't see the same one twice in a row.`
        : `**Three stars and the ${listed} fires** at the end of that turn.`,
      ...(reference ? ["The host can switch any of these off for a table."] : []),
      ...events.map((k) => CONSTELLATION_EVENT_RULES[k]),
      "Only a card that stays can light a star. A card lost on a wrong guess lights nothing.",
    ];
    sections.push({ title: "Constellations", items: constellationItems });
  }

  // On by default, and a catch-up rather than a tweak to the turn, so it is
  // spelled out on its own rather than buried in Winning & Scoring.
  if (reference || constellationRules.mercyRule) {
    sections.push({
      title: "Mercy Rule",
      items: [
        ...(reference ? ["On unless the host switches it off for a table."] : []),
        "**Fallen too far behind?** At the end of your turn, if your deck is at least half a deal bigger than the average of the other players still holding cards (20 at most), it bursts. Dealt 6, that's 3 cards more than the average.",
        "**A third of the gap, rounded up, flies off the top of your deck** onto random piles, spread across all five. It never takes you past the average.",
        "It's checked after every one of your turns, not only one where you took a pile. After a burst it rests for three of your turns.",
        "**It stops once anyone is out**, so the last lap is played as it stands.",
        "**The cards land one at a time, for real.** Nothing is guessed, but a wild fires and you lay its follow-up, and a card can light a Constellations star - even fire an event before the next turn.",
        "Only at a table of two or more.",
      ],
    });
  }

  // The Mercy Rule's companion catch-up, for a run of bad luck rather than a
  // big deck - spelled out on its own for the same reason.
  if (reference || constellationRules.droughtRule) {
    sections.push({
      title: "Drought",
      items: [
        ...(reference ? ["On unless the host switches it off for a table."] : []),
        `**Take a pile ${DROUGHT_MISSES_TO_MIST} turns in a row** and your deck is wrapped in a blue mist.`,
        "**The next pile you take, the mist fires at it** and bursts it from the middle: only the top half comes to you, never fewer than two cards.",
        "**The bottom half flies out onto the other four piles**, spread across them all. A wild in it stays with you.",
        "The mist waits for your next miss, however many good turns come first. Once it's used, it takes another run of misses to bring it back.",
        "Only at a table of two or more.",
      ],
    });
  }

  // The streak's own cost for being sat on, spelled out on its own as the two
  // catch-ups are: it is the one house rule that takes cards from the player
  // holding the fire rather than the one behind.
  if (reference || constellationRules.burningDeck) {
    sections.push({
      title: "Burning Deck",
      items: [
        ...(reference ? ["On unless the host switches it off for a table."] : []),
        `**Go ${STREAK_TURNS_TO_IGNITE + BURNING_DECK_FIRST_BURN_AFTER} turns in a row without taking a pile or spending your streak**, and the top card of your deck burns away as your next turn begins.`,
        "**Then every other turn it stays lit**, the next card burns. It's gone from the game: it lands on no pile, and a wild burned this way does nothing.",
        "**It never takes your last card**, so burning can't put you out. A Peek that was showing the burned card stays up and shows the next card.",
        "Spending the streak or taking a pile puts the fire out and starts the count again. Only where Streaks apply.",
      ],
    });
  }

  sections.push(
    {
      title: "Winning & Scoring",
      items: [
        "You go out **the instant your deck empties**, even part-way through a Five Out or a chain.",
        "The exception: a losing guess. If your last card comes back to you with a pile, you're not out.",
        "**Everyone gets the same number of turns.** Going out doesn't end the round there and then — every player who has had a turn fewer than you still takes it, so nobody loses to the seating order.",
        "That last turn is a real chance: **anyone who empties their deck in it goes out too**, level with you, and you share the win.",
        "**You're scored on your position, not your cards.** Going out is 1 point, the next-fewest cards is 2, and so on. The lowest score tops the board.",
        "**Ties share a place** and the next player takes the gap: with two players tied on 1 card each scoring 2, the player behind them scores 4.",
        ...(houseRules.vinnysRule
          ? [
              "**Vinny's Rule.** Down to your last card, you can only play it on the tallest pile — the other piles offer you nothing.",
              "**Tied for tallest?** Any of the tied piles will do; take your pick.",
              "The pile still decides what you can do there: a guess if it shows a value, a free placement if it's empty or wild-topped.",
            ]
          : []),
        "Rounds add up into a match. Play as many as you like — the match table is always there.",
      ],
    },
    {
      title: "A Tip",
      items: [
        "Taken piles flip as a unit onto the bottom of your deck, so the newest card ends up dead last. Remember that order and you'll know what's coming.",
      ],
    },
  );

  return sections;
}

/** Splits a bullet into its bold lead-in (if any) and the remaining text. */
function renderBullet(text: string, key: number) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <li key={key}>
      {parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
    </li>
  );
}

const SUBHEAD = "## ";

/** A section's entries as list items: `## ` entries become sub-headings, and what follows is indented under it. */
function renderItems(items: string[]) {
  let underSubhead = false;
  return items.map((text, key) => {
    if (text.startsWith(SUBHEAD)) {
      underSubhead = true;
      return (
        <li key={key} className="rules-subhead">
          {text.slice(SUBHEAD.length)}
        </li>
      );
    }
    const bullet = renderBullet(text, key);
    return underSubhead ? (
      <li key={key} className="rules-sub">
        {bullet.props.children}
      </li>
    ) : (
      bullet
    );
  });
}

/** Stable id for a section's bullet list, so its heading button can own it. */
function sectionId(title: string) {
  return `rules-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}

/**
 * How-to-play reference: a card-stock tray over a dimmed table, scrollable on
 * its own so it fits any viewport (bottom sheet on phones, centered on tablets
 * and desktops). Closes on the button, the backdrop, or Escape.
 *
 * Sections **start closed every time the sheet opens**, and open independently —
 * the whole sheet unrolled is several screens of bullets, and a player who came
 * in to settle one question about wilds had to scroll past the scoring to find
 * it. Closed, every heading is on the first screen and the answer is one tap
 * away. Nothing here is sequential, so there is no reading order to preserve.
 */
export function RulesModal({
  deckConfig,
  houseRules,
  onClose,
  reference = false,
  feedback = false,
  composition,
}: {
  deckConfig?: DeckConfig;
  /**
   * The deck in play, already worded, for a table not dealt from a deck size:
   * a campaign level's small pool. Replaces the size-derived composition line.
   */
  composition?: string;
  houseRules?: HouseRules;
  /** Explain every rule whatever the table, for the main menu where there is no table. */
  reference?: boolean;
  /**
   * Put the feedback button in the sheet's head. The online table's HUD strip
   * is already five wide on a narrow phone, so this sheet - the "?" every table
   * has - is where feedback lives there.
   */
  feedback?: boolean;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const sections = buildSections(
    deckConfig ?? DEFAULT_DECK_CONFIG,
    reference ? { vinnysRule: true, jourdansStreaks: true, blueShell: true, starfall: true, mercyRule: true, droughtRule: true, burningDeck: true } : normalizeHouseRules(houseRules),
    reference,
    composition,
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const allOpen = sections.every((s) => open[s.title]);

  const toggle = (title: string, section: HTMLElement | null) => {
    const opening = !open[title];
    setOpen((prev) => ({ ...prev, [title]: !prev[title] }));
    // A heading near the bottom of the sheet expands below the fold, so the tap
    // reads as having done nothing. Click is a discrete event, so React has
    // already committed the bullets by the time this frame runs. `nearest`
    // leaves a section that already fits exactly where it is.
    if (opening && section) {
      const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      requestAnimationFrame(() =>
        section.scrollIntoView({ block: "nearest", behavior: smooth ? "smooth" : "auto" }),
      );
    }
  };

  // Once, on open - never again on a re-render. The online table passes an
  // inline onClose and re-renders on every clock tick, and re-running this
  // effect pulled focus back to the panel each time, out of the feedback box
  // the sheet holds. Escape reads the latest onClose through the ref instead.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    window.addEventListener("keydown", onKey);
    // Keep the page behind the sheet from scrolling along with it.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div className="rules-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        className="rules-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rules-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rules-head">
          <div>
            <div className="eyebrow">Higher &middot; Lower &middot; Match</div>
            <h2 id="rules-title" className="title rules-title">
              How to Play
            </h2>
          </div>
          <div className="rules-head-actions">
            {feedback && <FeedbackButton variant="hud" />}
            <button className="hud-exit rules-close" aria-label="Close rules" onClick={onClose}>
              &#10005;
            </button>
          </div>
        </div>

        <div className="rules-body">
          {sections.map((s) => {
            const isOpen = !!open[s.title];
            const id = sectionId(s.title);
            return (
              <section key={s.title} className={`rules-section rules-fold${isOpen ? " is-open" : ""}`}>
                <button
                  type="button"
                  className="section-label rules-toggle"
                  aria-expanded={isOpen}
                  aria-controls={id}
                  onClick={(e) => toggle(s.title, e.currentTarget.parentElement)}
                >
                  {s.title}
                  <span className="rules-chevron" aria-hidden="true" />
                </button>
                {isOpen && (
                  <ul id={id} className="rules-list">
                    {renderItems(s.items)}
                  </ul>
                )}
              </section>
            );
          })}
        </div>

        <div className="rules-foot">
          {/* Somebody reading the sheet end to end shouldn't have to open eight
              headings to do it. */}
          <button className="btn-small rules-expand" onClick={() => setOpen(allOpen ? {} : Object.fromEntries(sections.map((s) => [s.title, true])))}>
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
          <button className="primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
